import { Injectable } from '@angular/core';
import { CompletionMessage } from '@app-libs/model';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { OpenaiAPIService } from '../../api/openai-api.service';
import { ConfigurationClientService } from '../../client/configuration-client.service';
import { IConversationTitleService } from './conversation.interfaces';

/**
 * ConversationTitleService - Handles conversation title generation
 *
 * This service handles title generation, migrated from ChatApiService
 * with the exact same logic to preserve functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class ConversationTitleService implements IConversationTitleService {
  constructor(
    private configService: ConfigurationClientService,
    private openAIService: OpenaiAPIService,
  ) { }

  /**
   * Generate title for conversation based on messages
   * (Migrated from ChatApiService.generateConversationTitle - exact same logic)
   */
  generateTitle(messages: CompletionMessage[]): Observable<string | null> {
    console.log('Generating conversation title...');

    // Get user messages only
    const userMessages = messages.filter((message) => message.role === 'user');

    // Check if we should generate a title based on message count
    const triggerLengths = this.configService.getSetting('models', 'triggerHeaderUpdateLengths');

    // Ensure triggerLengths is an array
    if (!Array.isArray(triggerLengths) || !triggerLengths.includes(userMessages.length)) {
      return of(null);
    }

    // Filter messages for title generation
    const filteredMessages = messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .filter((message) => !message.file_id) // Exclude file messages
      .map((message) => ({
        role: message.role,
        content: message.content,
      }));

    // Add title generation prompt
    filteredMessages.push({
      role: 'user',
      content: this.configService.BUILD_CONVERSATION_TITLE,
    });

    // Generate title using AI
    return this.openAIService
      .createCompletionAsText(
        this.configService.BUILD_CONVERSATION_TITLE,
        filteredMessages,
        this.configService.getSetting('models', 'buildTitleModel'),
      )
      .pipe(
        timeout(30000), // 30 second timeout
        map((title) => title?.replaceAll('"', '').trim() || null),
        catchError((error) => {
          console.error('Failed to generate title:', error);
          return of(null);
        }),
      );
  }

  /**
   * Check if title should be generated based on message count
   */
  shouldGenerateTitle(messageCount: number): boolean {
    const triggerLengths = this.configService.getSetting('models', 'triggerHeaderUpdateLengths');
    return Array.isArray(triggerLengths) && triggerLengths.includes(messageCount);
  }
}
